import { useMemo, useState } from 'react';
import { Info, X } from 'lucide-react';
import { useBalanceStats } from './useSectorTaxonomy';
import { shiftIsoDate, todayIsoDate } from './sectorTaxonomy.types';
import type { SectorBalanceStatsItem } from '../../api/sectorTaxonomyApi';

// 권위: ARCH_DASHBOARD §4.11 탭 D.
// 기본 전체 시계열 7종 + 중복 지표 5종 (§4.11 확장, 2026-04).

type NumericMetricKey = Exclude<keyof SectorBalanceStatsItem, 'statDate'>;

interface MetricSpec {
  key: NumericMetricKey;
  label: string;
  format: (v: number) => string;
  // 시계열 차트 색상 (CSS 변수 또는 hex). 토큰에 없는 색상은 tokens.css 에 등록된 것만 사용.
  lineClass?: string;
}

// 전반적 밸런싱 지표 — 기존 7종 (consolidation/avg/stddev 등).
const GENERAL_METRICS: MetricSpec[] = [
  { key: 'totalCanonicalSectors',    label: '정규 섹터 수',                format: (v) => v.toFixed(0) },
  { key: 'totalSourceSectors',       label: '원천 섹터 수',                format: (v) => v.toFixed(0) },
  { key: 'consolidationRatio',       label: '통합도 (canonical/source)',   format: (v) => v.toFixed(3) },
  { key: 'avgSymbolsPerCanonical',   label: '섹터당 평균 종목 수',         format: (v) => v.toFixed(2) },
  { key: 'stddevSymbolsPerCanonical', label: '섹터 종목 수 표준편차',      format: (v) => v.toFixed(2) },
  { key: 'canonicalOverlapScore',    label: 'overlap score (SUM Jaccard)', format: (v) => v.toFixed(3) },
  { key: 'unmappedSourceCount',      label: '미매핑 원천 섹터',            format: (v) => v.toFixed(0) },
  { key: 'newSourceSectors30d',      label: '신규 원천 (30d)',             format: (v) => v.toFixed(0) },
];

// 중복(overlap) 지표 5종 — 감도 높은 시계열·리밸런싱 타이밍 판단용.
interface OverlapMetricSpec extends MetricSpec {
  tooltip: string;    // ⓘ hover 시 짧은 설명.
  lineClass: string;  // 시계열 차트 전용 색상 클래스 (tx-chart__line--*).
}

const OVERLAP_METRICS: OverlapMetricSpec[] = [
  {
    key: 'highOverlapPairCount70',
    label: '극심 중복 쌍 (≥0.70)',
    format: (v) => v.toFixed(0),
    tooltip:
      '구성 종목의 70% 이상이 겹치는 섹터 쌍 수. 0 에 가까울수록 좋습니다. Type-A 리밸런싱 후 대부분 0이 되어야 합니다.',
    lineClass: 'tx-chart__line--danger',
  },
  {
    key: 'highOverlapPairCount50',
    label: '중복 의심 쌍 (≥0.50)',
    format: (v) => v.toFixed(0),
    tooltip: 'Jaccard 50% 이상 중복 섹터 쌍. 운영자 검토 대상.',
    lineClass: 'tx-chart__line--warn',
  },
  {
    key: 'topOverlapMax',
    label: '최대 Jaccard',
    format: (v) => v.toFixed(3),
    tooltip:
      '가장 중복된 한 쌍의 유사도. 0.5 미만이면 안정, 0.7 이상이면 즉시 병합 검토.',
    lineClass: 'tx-chart__line--info',
  },
  {
    key: 'top10OverlapAvg',
    label: '상위 10쌍 평균',
    format: (v) => v.toFixed(3),
    tooltip: '중복 hotspot 강도. 리밸런싱 전후 비교에 유용.',
    lineClass: 'tx-chart__line--accent2',
  },
  {
    key: 'weightedOverlapScore',
    label: '가중 중복 점수',
    format: (v) => v.toFixed(3),
    tooltip:
      '종목수 가중 평균. 작은 섹터의 영향을 줄인 포트폴리오 중복 비중.',
    lineClass: 'tx-chart__line--ok',
  },
];

const PRESETS = [
  { id: '30d',  label: '30일',  days: 30 },
  { id: '90d',  label: '90일',  days: 90 },
  { id: '180d', label: '180일', days: 180 },
  { id: '1y',   label: '1년',   days: 365 },
];

// 탭 D — 밸런싱 지표 시계열 차트 + 요약 + 중복 지표 + 도움말.
export function BalanceChartsTab(): JSX.Element {
  const [preset, setPreset] = useState<string>('90d');
  const [from, setFrom] = useState<string>(shiftIsoDate(90));
  const [to, setTo] = useState<string>(todayIsoDate());
  const [helpOpen, setHelpOpen] = useState(false);

  const applyPreset = (id: string): void => {
    const p = PRESETS.find((x) => x.id === id);
    if (!p) return;
    setPreset(id);
    setFrom(shiftIsoDate(p.days));
    setTo(todayIsoDate());
  };

  const query = useBalanceStats(from, to);
  const series = query.data ?? [];
  const latest = series.length > 0 ? series[series.length - 1] : null;

  const unmappedAlert = useMemo(() => {
    if (series.length === 0) return false;
    const lastWeek = series.slice(-7);
    return lastWeek.length >= 7 && lastWeek.every((s) => s.unmappedSourceCount > 0);
  }, [series]);

  return (
    <div>
      <div className="pca-filter">
        <div className="pca-filter__group">
          <label className="pca-filter__label">기간 프리셋</label>
          <div className="pca-chip-row">
            {PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                className={'pca-chip' + (preset === p.id ? ' pca-chip--active' : '')}
                onClick={() => applyPreset(p.id)}
              >
                {p.label}
              </button>
            ))}
            <button
              type="button"
              className={'pca-chip' + (preset === 'custom' ? ' pca-chip--active' : '')}
              onClick={() => setPreset('custom')}
            >
              Custom
            </button>
          </div>
        </div>
        <div className="pca-filter__group">
          <label className="pca-filter__label">from</label>
          <input
            type="date"
            className="pca-input"
            value={from}
            onChange={(e) => {
              setPreset('custom');
              setFrom(e.target.value);
            }}
          />
        </div>
        <div className="pca-filter__group">
          <label className="pca-filter__label">to</label>
          <input
            type="date"
            className="pca-input"
            value={to}
            onChange={(e) => {
              setPreset('custom');
              setTo(e.target.value);
            }}
          />
        </div>
      </div>

      {unmappedAlert && (
        <div className="tx-warn-banner" style={{ marginTop: 8 }}>
          최근 7일간 미매핑 원천 섹터가 지속 발생 (Proposal §2.6 경고). 리뷰 큐
          &gt; 미매핑 fallback 탭을 확인하세요.
        </div>
      )}

      {query.isError && (
        <div className="pca-warning-banner" style={{ marginTop: 8 }}>
          차트 데이터 로드 실패: {(query.error as Error).message}
        </div>
      )}

      {/* ── 최신값 요약: 기존 전체 밸런싱 지표 + 중복 지표 5종 ── */}
      <div className="tx-bal-summary" data-testid="tx-bal-summary">
        <section className="tx-bal-card" aria-label="전체 밸런싱 지표">
          <header className="tx-bal-card__head">
            <h3 className="tx-bal-card__title">전체 밸런싱 지표</h3>
            <span className="tx-bal-card__sub">
              {latest ? `기준일: ${latest.statDate}` : '데이터 없음'}
            </span>
          </header>
          <dl className="tx-bal-grid">
            {GENERAL_METRICS.map((m) => {
              const raw = latest ? (latest[m.key] as number | null | undefined) : null;
              const display =
                raw === null || raw === undefined || Number.isNaN(raw) ? '—' : m.format(Number(raw));
              return (
                <div key={String(m.key)} className="tx-bal-item">
                  <dt className="tx-bal-item__label">{m.label}</dt>
                  <dd
                    className="tx-bal-item__value"
                    data-testid={`tx-bal-general-${String(m.key)}`}
                  >
                    {display}
                  </dd>
                </div>
              );
            })}
          </dl>
        </section>

        <section className="tx-bal-card" aria-label="중복 지표">
          <header className="tx-bal-card__head">
            <h3 className="tx-bal-card__title">중복 지표</h3>
            <button
              type="button"
              className="tx-bal-help-btn"
              onClick={() => setHelpOpen(true)}
              aria-label="중복 지표 도움말 열기"
              data-testid="tx-bal-help-open"
            >
              <Info size={14} strokeWidth={1.75} aria-hidden="true" />
              도움말
            </button>
          </header>
          <dl className="tx-bal-grid tx-bal-grid--overlap">
            {OVERLAP_METRICS.map((m) => {
              const raw = latest ? (latest[m.key] as number | null | undefined) : null;
              const display =
                raw === null || raw === undefined || Number.isNaN(raw) ? '—' : m.format(Number(raw));
              return (
                <div key={String(m.key)} className="tx-bal-item">
                  <dt className="tx-bal-item__label">
                    {m.label}
                    <InfoTooltip text={m.tooltip} metricKey={String(m.key)} />
                  </dt>
                  <dd
                    className="tx-bal-item__value"
                    data-testid={`tx-bal-overlap-${String(m.key)}`}
                  >
                    {display}
                  </dd>
                </div>
              );
            })}
          </dl>
        </section>
      </div>

      {/* ── 시계열 차트 — 전체 + 중복 지표 ── */}
      <h3 className="tx-bal-section-title">전체 지표 시계열</h3>
      <div className="tx-charts-grid">
        {GENERAL_METRICS.map((m) => (
          <MetricChart
            key={m.key as string}
            title={m.label}
            series={series}
            valueKey={m.key}
            format={m.format}
            loading={query.isLoading}
          />
        ))}
      </div>

      <h3 className="tx-bal-section-title">중복 지표 시계열</h3>
      <div className="tx-charts-grid">
        {OVERLAP_METRICS.map((m) => (
          <MetricChart
            key={m.key as string}
            title={m.label}
            series={series}
            valueKey={m.key}
            format={m.format}
            loading={query.isLoading}
            lineClass={m.lineClass}
          />
        ))}
      </div>

      {helpOpen && <OverlapHelpPanel onClose={() => setHelpOpen(false)} />}
    </div>
  );
}

// ── 작은 ⓘ 툴팁 — CSS-only hover/focus, 키보드 접근 가능. ──
interface InfoTooltipProps {
  text: string;
  metricKey: string;
}

function InfoTooltip({ text, metricKey }: InfoTooltipProps): JSX.Element {
  return (
    <span
      className="tx-bal-info"
      tabIndex={0}
      role="note"
      aria-label={text}
      data-testid={`tx-bal-tip-${metricKey}`}
    >
      <Info size={12} strokeWidth={1.75} aria-hidden="true" />
      <span className="tx-bal-info__bubble" role="tooltip">
        {text}
      </span>
    </span>
  );
}

// ── 도움말 패널 — 우측 슬라이드 오버레이. 모달과 달리 읽기 전용. ──
function OverlapHelpPanel({ onClose }: { onClose: () => void }): JSX.Element {
  return (
    <div
      className="tx-help-backdrop"
      role="presentation"
      onClick={onClose}
      data-testid="tx-bal-help-panel"
    >
      <aside
        className="tx-help-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tx-help-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="tx-help-panel__head">
          <h2 id="tx-help-title" className="tx-help-panel__title">
            섹터 밸런싱 지표 가이드
          </h2>
          <button
            type="button"
            className="tx-help-panel__close"
            onClick={onClose}
            aria-label="도움말 닫기"
            data-testid="tx-bal-help-close"
          >
            <X size={16} strokeWidth={1.75} aria-hidden="true" />
          </button>
        </header>
        <div className="tx-help-panel__body">
          <section>
            <h3>목적</h3>
            <p>
              섹터 분류 체계의 <strong>중복(overlap) 정도</strong>를 추적해
              리밸런싱 타이밍을 판단합니다.
            </p>
          </section>

          <section>
            <h3>해석 방법</h3>
            <ul>
              <li>
                <strong>낮은 중복 (≈0)</strong> = 섹터들이 서로 다른 종목을 다룸
                → 분류 체계 깔끔
              </li>
              <li>
                <strong>높은 중복 (≥0.5)</strong> = 여러 섹터가 같은 종목을
                공유 → 병합 필요
              </li>
            </ul>
          </section>

          <section>
            <h3>변화 패턴</h3>
            <table className="tx-help-table">
              <thead>
                <tr>
                  <th>시점</th>
                  <th>예상 양상</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>auto_seed 직후</td>
                  <td>
                    high_50=18, max=0.79 — 네이버 원천 그대로라 중복 많음
                  </td>
                </tr>
                <tr>
                  <td>Type-A 리밸런싱 후</td>
                  <td>
                    high_70=0, high_50&lt;5, max&lt;0.5 — 의미 기반 병합으로
                    정돈
                  </td>
                </tr>
                <tr>
                  <td>시간 경과 (신규 테마 편입)</td>
                  <td>다시 상승 추세 → 재리밸런싱 타이밍</td>
                </tr>
              </tbody>
            </table>
          </section>

          <section>
            <h3>지표별 상세</h3>
            <dl className="tx-help-defs">
              <dt>극심 중복 쌍 (≥0.70)</dt>
              <dd>
                구성 종목의 70% 이상이 겹치는 섹터 쌍 수. 0 에 가까울수록
                좋습니다. Type-A 리밸런싱 후 대부분 0이 되어야 합니다.
              </dd>
              <dt>중복 의심 쌍 (≥0.50)</dt>
              <dd>Jaccard 50% 이상 중복 섹터 쌍. 운영자 검토 대상.</dd>
              <dt>최대 Jaccard</dt>
              <dd>
                가장 중복된 한 쌍의 유사도. 0.5 미만이면 안정, 0.7 이상이면
                즉시 병합 검토.
              </dd>
              <dt>상위 10쌍 평균</dt>
              <dd>중복 hotspot 강도. 리밸런싱 전후 비교에 유용.</dd>
              <dt>가중 중복 점수</dt>
              <dd>
                수식 <code>Σ(jaccard × 교집합종목수) / Σ(교집합종목수)</code> —
                작은 섹터들의 중복이 점수를 왜곡하는 것을 방지. 실제 포트폴리오에
                미치는 중복 영향도에 가깝습니다.
              </dd>
            </dl>
          </section>

          <section>
            <h3>참고</h3>
            <ul>
              <li>Jaccard = 교집합 / 합집합 (0~1)</li>
              <li>
                원천: <code>sector_similarity</code> 테이블, canonical scope
              </li>
              <li>계산 주기: 매일 04:45 KST</li>
            </ul>
          </section>
        </div>
      </aside>
    </div>
  );
}

interface MetricChartProps {
  title: string;
  series: SectorBalanceStatsItem[];
  valueKey: NumericMetricKey;
  format: (v: number) => string;
  loading: boolean;
  lineClass?: string;
}

function MetricChart({
  title,
  series,
  valueKey,
  format,
  loading,
  lineClass,
}: MetricChartProps): JSX.Element {
  const data = useMemo(() => {
    return series
      .map((s) => {
        const v = s[valueKey];
        const num = v === null || v === undefined ? null : Number(v);
        return { date: s.statDate, value: num };
      })
      .filter((d): d is { date: string; value: number } =>
        d.value !== null && !Number.isNaN(d.value)
      );
  }, [series, valueKey]);

  const stats = useMemo(() => {
    if (data.length === 0) return null;
    const values = data.map((d) => d.value);
    const last = values[values.length - 1];
    const min = Math.min(...values);
    const max = Math.max(...values);
    return { last, min, max };
  }, [data]);

  // SVG 좌표 — width 600 x height 200 viewBox.
  const width = 600;
  const height = 200;
  const padding = { top: 12, right: 8, bottom: 24, left: 40 };
  const plotW = width - padding.left - padding.right;
  const plotH = height - padding.top - padding.bottom;

  const linePath = useMemo(() => {
    if (data.length === 0 || !stats) return '';
    const { min, max } = stats;
    const spanY = max - min || 1;
    return data
      .map((d, i) => {
        const x = padding.left + (data.length === 1 ? plotW / 2 : (i * plotW) / (data.length - 1));
        const y = padding.top + plotH - ((d.value - min) / spanY) * plotH;
        return `${i === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
      })
      .join(' ');
  }, [data, stats, plotH, plotW, padding.left, padding.top]);

  const pathClass = 'tx-chart__line' + (lineClass ? ` ${lineClass}` : '');

  return (
    <div className="tx-chart" data-testid={`tx-chart-${String(valueKey)}`}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <strong>{title}</strong>
        {stats && (
          <span className="pca-muted" style={{ fontVariantNumeric: 'tabular-nums' }}>
            last {format(stats.last)} · min {format(stats.min)} · max {format(stats.max)}
          </span>
        )}
      </div>
      <svg
        className="tx-chart__svg"
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        aria-label={`${title} 시계열`}
      >
        <line
          className="tx-chart__axis"
          x1={padding.left}
          y1={padding.top + plotH}
          x2={padding.left + plotW}
          y2={padding.top + plotH}
        />
        <line
          className="tx-chart__axis"
          x1={padding.left}
          y1={padding.top}
          x2={padding.left}
          y2={padding.top + plotH}
        />
        {data.length > 0 && stats && (
          <>
            <path d={linePath} className={pathClass} />
            {data.length === 1 && (
              <circle
                className="tx-chart__dot"
                cx={padding.left + plotW / 2}
                cy={padding.top + plotH / 2}
                r={2.5}
              />
            )}
          </>
        )}
        {loading && (
          <text x={width / 2} y={height / 2} textAnchor="middle" className="pca-muted">
            로딩 중…
          </text>
        )}
        {!loading && data.length === 0 && (
          <text x={width / 2} y={height / 2} textAnchor="middle" className="pca-muted">
            데이터 없음
          </text>
        )}
      </svg>
    </div>
  );
}
